import React, { useState, useEffect } from "react";
import { createPortal } from "react-dom";
import { loadStripe } from "@stripe/stripe-js";
import {
  Elements,
  CardElement,
  useStripe,
  useElements
} from "@stripe/react-stripe-js";
import { motion, AnimatePresence } from "framer-motion";
import { usePaymentMethods } from "../../contexts/PaymentMethodsContext";
import { Button } from "../ui/Button";
import { X, CreditCard, Loader2, Shield, CheckCircle, AlertCircle } from "lucide-react";
import { apiService } from "../../services/paymentAPI";

const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY!);

const CardPaymentMethodForm: React.FC<{ onClose: () => void; onSuccess?: (paymentMethod: any) => void }> = ({ onClose, onSuccess }) => {
  const stripe = useStripe();
  const elements = useElements();
  const { addPaymentMethod } = usePaymentMethods();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    setMounted(true);
    // Prevent body scroll when modal is open
    document.body.style.overflow = "hidden";
    
    return () => {
      document.body.style.overflow = "unset";
    };
  }, []);

  // Reset states when modal closes
  const handleClose = () => {
    setSuccess(false);
    setError(null);
    setLoading(false);
    document.body.style.overflow = "unset";
    onClose();
  };

  if (!mounted) return null;

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();

    if (!stripe || !elements) {
      return;
    }

    setLoading(true);
    setError(null);

    try {
      // Create setup intent
      const setupIntentResponse = await apiService.createSetupIntent();
      const { client_secret } = setupIntentResponse;

      // Confirm setup intent
      const { error: stripeError, setupIntent } = await stripe.confirmCardSetup(
        client_secret,
        {
          payment_method: {
            card: elements.getElement(CardElement)!,
            billing_details: {
              name: "Card Holder Name", // Get from user profile
            },
          },
        }
      );

      if (stripeError) {
        setError(stripeError.message || "An error occurred");
        return;
      }

      if (setupIntent?.status === "succeeded") {
        const stripePaymentMethodId = setupIntent.payment_method as string;
        
        // Add payment method to backend
        await addPaymentMethod({
          stripePaymentMethodId,
          type: "card",
        });

        setSuccess(true);
        
        // Call onSuccess callback if provided
        // The parent component will refresh payment methods and find the newly created one
        if (onSuccess) {
          // Pass the Stripe payment method ID so parent can identify it
          onSuccess({ stripePaymentMethodId });
        }
        
        setTimeout(() => {
          handleClose();
        }, 2000);
      }
    } catch (err) {
      setError("Failed to add payment method");
    } finally {
      setLoading(false);
    }
  };

  return createPortal(
    <div 
      className="fixed inset-0 bg-black bg-opacity-50 z-[9999] flex items-center justify-center p-4"
      onClick={(e) => e.target === e.currentTarget && handleClose()}
      style={{ backdropFilter: "blur(4px)" }}
    >
      <motion.div
        initial={{ scale: 0.95, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.95, opacity: 0 }}
        transition={{ duration: 0.2, ease: "easeOut" }}
        className="bg-white rounded-2xl w-full max-w-md max-h-[90vh] overflow-y-auto shadow-2xl mx-4"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="bg-gradient-to-r from-blue-600 to-blue-700 px-6 py-4 rounded-t-2xl">
          <div className="flex justify-between items-center">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-white/20 rounded-lg">
                <CreditCard className="h-6 w-6 text-white" />
              </div>
              <div>
                <h2 className="text-xl font-bold text-white">Add Credit Card</h2>
                <p className="text-blue-100 text-sm">Secure payment method</p>
              </div>
            </div>
            <button
              onClick={handleClose}
              className="p-2 hover:bg-white/20 rounded-lg transition-colors"
            >
              <X className="h-5 w-5 text-white" />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="p-6">

          {success ? (
            <motion.div
              initial={{ scale: 0.8, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              className="text-center py-8"
            >
              <div className="mx-auto w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mb-4">
                <CheckCircle className="h-8 w-8 text-green-600" />
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-2">Card Added Successfully!</h3>
              <p className="text-gray-600">Your credit card has been added securely.</p>
            </motion.div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Security Notice */}
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <div className="flex items-start gap-3">
                  <Shield className="h-5 w-5 text-blue-600 flex-shrink-0 mt-0.5" />
                  <div>
                    <h4 className="text-sm font-medium text-blue-800">Secure & Encrypted</h4>
                    <p className="text-sm text-blue-700 mt-1">
                      Your card information is encrypted and processed securely by Stripe.
                    </p>
                  </div>
                </div>
              </div>

              {/* Card Element */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-3">
                  Card Information
                </label>
                <div className="border-2 border-gray-200 rounded-xl p-4 focus-within:border-blue-500 transition-colors">
                  <CardElement
                    options={{
                      style: {
                        base: {
                          fontSize: "16px",
                          color: "#374151",
                          fontFamily: "system-ui, sans-serif",
                          "::placeholder": {
                            color: "#9CA3AF",
                          },
                        },
                        invalid: {
                          color: "#EF4444",
                        },
                      },
                      hidePostalCode: true,
                    }}
                  />
                </div>
              </div>
              
              {/* Error Message */}
              <AnimatePresence>
                {error && (
                  <motion.div
                    initial={{ opacity: 0, y: -10 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -10 }}
                    className="bg-red-50 border border-red-200 rounded-lg p-4"
                  >
                    <div className="flex items-start gap-3">
                      <AlertCircle className="h-5 w-5 text-red-600 flex-shrink-0 mt-0.5" />
                      <div>
                        <h4 className="text-sm font-medium text-red-800">Error</h4>
                        <p className="text-sm text-red-700 mt-1">{error}</p>
                      </div>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
              
              {/* Action Buttons */}
              <div className="flex gap-3 pt-4">
                <Button
                  type="button"
                  variant="outline"
                  onClick={handleClose}
                  className="flex-1 h-12"
                  disabled={loading}
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  disabled={!stripe || loading}
                  className="flex-1 h-12 bg-blue-600 hover:bg-blue-700"
                >
                  {loading ? (
                    <>
                      <Loader2 className="h-4 w-4 animate-spin mr-2" />
                      Adding Card...
                    </>
                  ) : (
                    <>
                      <CreditCard className="h-4 w-4 mr-2" />
                      Add Card
                    </>
                  )}
                </Button>
              </div>
            </form>
          )}
        </div>
      </motion.div>
    </div>,
    document.body
  );
};

export default CardPaymentMethodForm;

export const CardPaymentMethodFormWrapper: React.FC<{ onClose: () => void }> = ({ onClose }) => (
  <Elements stripe={stripePromise}>
    <CardPaymentMethodForm onClose={onClose} />
  </Elements>
);
